import React, { useState, useEffect, forwardRef } from 'react';
import styled from 'styled-components';
import { Drawer, useMediaQuery } from '@mui/material';
import Colors from '../global/Colors';
import Fonts from '../global/Fonts';
import Icon from '../global/Icon';
import ButtonMicro from './ButtonMicro';
import Badge from './Badge';
import Tooltip from './Tooltip';

const PanelContainer = styled.div<{ $colorMode: 'light' | 'dark', $fullBleed: boolean }>`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.$colorMode === 'dark' ? Colors.grey9 : Colors.background};
  border: 1px solid ${props => props.$fullBleed ? 'transparent' : props.$colorMode === 'dark' ? Colors.grey8 : Colors.grey1};
  border-radius: 4px;
  overflow: visible;
`;

const PanelTitle = styled.div<{ $colorMode: 'light' | 'dark' }>`
  position: sticky;
  top: 0;  
  background: ${props => props.$colorMode === 'dark' 
        ? `linear-gradient(to bottom, ${Colors.grey9} 0%, ${Colors.grey9} 85%, transparent 100%)`
        : `linear-gradient(to bottom, ${Colors.background} 0%, ${Colors.background} 85%, transparent 100%)`};
  color: ${props => props.$colorMode === 'dark' ? Colors.white : Colors.black};
  padding: 6px 5px 5px 5px;
  z-index: 1;
`;

const PanelContent = styled.div<{ $colorMode: 'light' | 'dark'; $position: 'left' | 'right' | 'bottom'; $fullBleed: boolean }>`
  flex: 1;
  color: ${props => props.$colorMode === 'dark' ? Colors.white : Colors.black};
  font-family: ${Fonts.lexendMedium.fontFamily};
  font-weight: ${Fonts.lexendMedium.fontWeight};
  padding: ${props => props.$fullBleed ? '10px' : '12px'};
  overflow-y: auto;
  
  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: ${props => props.$colorMode === 'dark' ? Colors.grey7 : Colors.grey2};
  
  /* Webkit scrollbar (Chrome, Safari, Edge) */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: ${props => props.$colorMode === 'dark' ? Colors.grey9 : Colors.background};
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.$colorMode === 'dark' ? Colors.grey7 : Colors.grey2};
    border-radius: 4px;
  }
`;

const ToggleButtonContainer = styled.div<{ 
  $position: 'left' | 'right' | 'bottom',
  $isOpen: boolean,
  $isCollapsed: boolean
}>`
  position: ${props => props.$isCollapsed ? 'fixed' : 'absolute'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 14px;
  min-height: 20px;
  z-index: 10;
  
  /* Position on outer edge */
  ${props => props.$position === 'left' && !props.$isCollapsed ? 'right: -18px;' : ''}
  ${props => props.$position === 'right' && !props.$isCollapsed ? 'left: -18px;' : ''}
  ${props => props.$position === 'bottom' && !props.$isCollapsed ? 'top: -12px;' : ''}
  
  /* Position when fully collapsed */
  ${props => props.$position === 'left' && props.$isCollapsed ? 'left: 0px;' : ''}
  ${props => props.$position === 'right' && props.$isCollapsed ? 'right: 0px;' : ''}
  ${props => props.$position === 'bottom' && props.$isCollapsed ? 'bottom: 0px;' : ''}
  
  /* Center vertically for left/right, horizontally for bottom */
  ${props => (props.$position === 'left' || props.$position === 'right') && !props.$isCollapsed ? 'top: 50%; transform: translateY(-50%);' : ''}
  ${props => props.$position === 'bottom' && !props.$isCollapsed ? 'left: 50%; transform: translateX(-50%);' : ''}
  ${props => (props.$position === 'left' || props.$position === 'right') && props.$isCollapsed ? 'top: 50%; transform: translateY(-50%);' : ''}
  ${props => props.$position === 'bottom' && props.$isCollapsed ? 'left: 50%; transform: translateX(-50%);' : ''}
`;

export interface MenuPanelProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    position?: 'left' | 'right' | 'bottom';
    colorMode?: 'light' | 'dark';
    width?: number | string; // The width of the panel when expanded (for left/right positions)
    collapsedWidth?: number;
    height?: number | string; // The height of the panel when expanded (for bottom position)
    collapsedHeight?: number;
    title?: React.ReactNode; // Optional title that will be pinned to the top while scrolling
    children: React.ReactNode;
    expandTooltip?: string;
    badge?: {
        content: React.ReactNode;
        vertical?: 'top' | 'bottom';
        horizontal?: 'left' | 'right';
        invisible?: boolean;
        max?: number;
        showZero?: boolean;
    };
    isCollapsible?: boolean;
    overlay?: boolean;
    fullBleed?: boolean;
}

const MenuPanel = forwardRef<HTMLDivElement, MenuPanelProps>(({
  open,
  onOpenChange,
  position = 'left',
  width,
  height = 300,
  collapsedWidth = 0,
  collapsedHeight = 60,
  children,
  title,
  colorMode = 'dark',
  expandTooltip,
  isCollapsible = true,
  badge,
  overlay = false,
  fullBleed = false,
}, ref) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isFullyCollapsed = (!open && collapsedWidth === 0 && position !== 'bottom') || 
                          (!open && collapsedHeight === 0 && position === 'bottom');
  
  const getToggleIcon = () => {
    if (position === 'left') {
      return open ? 'left' : 'right';
    } else if (position === 'right') {
      return open ? 'right' : 'left';
    } else { // bottom
      return open ? 'down' : 'up';
    }
  };

  const getPanelSize = () => {
    if (position === 'bottom') {
      return {
        height: open ? height : collapsedHeight,
        width: '100%'
      };
    } else {
      return {
        width: open ? width : collapsedWidth,
        height: '100%'
      };
    }
  };

  const handleToggle = () => {
    if (isCollapsible) {
      onOpenChange(!open);
    }
  };

  const renderToggleButton = () => {
    if (!isCollapsible) return null;
    
    return (
      <ToggleButtonContainer 
        $position={position} 
        $isOpen={open} 
        $isCollapsed={isFullyCollapsed}
      >
        {badge ? (
          <Badge
            content={badge.content}
            colorMode={colorMode}
            vertical={badge.vertical || 'top'}
            horizontal={badge.horizontal || 'right'}
            invisible={badge.invisible}
            max={badge.max}
            showZero={badge.showZero}
          >
            {renderButton()}
          </Badge>
        ) : (
          renderButton()
        )}
      </ToggleButtonContainer>
    );
  };

  const renderButton = () => {
    return (
      <Tooltip 
        colorMode={colorMode}
        text={expandTooltip} 
        position={position === 'left' ? 'right' : position === 'right' ? 'left' : 'top'} 
        hide={expandTooltip === null}
      >
        <ButtonMicro 
          icon={getToggleIcon()} 
          onClick={handleToggle} 
          colorMode={colorMode}
          size={16} 
          borderRadius={
            position === 'right' ? '4px 0 0 4px' : 
            position === 'left' ? '0 4px 4px 0' : 
            position === 'bottom' ? '4px 4px 0 0' : 
            '4px'
          }
        />
      </Tooltip>
    )
  };

  // If fully collapsed, only render the toggle button
  if (isFullyCollapsed) {
    return renderToggleButton();
  }

  return (
    <Drawer
      ref={ref}
      variant={isMobile || overlay ? 'temporary' : 'permanent'}
      open={isMobile ? open : true}
      onClose={() => isMobile && onOpenChange(false)}
      anchor={position}
      sx={{
        width: position !== 'bottom' ? getPanelSize().width : '100%',
        height: position === 'bottom' ? getPanelSize().height : '100%',
        padding: fullBleed ? '0px' : position === 'right' || position === 'left' ? '5px 0px 10px 0px' : '10px 5px 0px 5px',
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: position !== 'bottom' ? getPanelSize().width : '100%',
          height: position === 'bottom' ? getPanelSize().height : '100%',
          boxSizing: 'border-box',
          backgroundColor: 'transparent',
          transition: 'width 0.3s ease, height 0.3s ease',
          overflow: 'visible',
          position: isMobile ? 'fixed' : 'relative',
        },
      }}
    >
      <PanelContainer $colorMode={colorMode} $fullBleed={fullBleed}>
        {renderToggleButton()}
        {title && <PanelTitle $colorMode={colorMode}>{title}</PanelTitle>}
        <PanelContent $colorMode={colorMode} $position={position} $fullBleed={fullBleed}>
          {children}
        </PanelContent>
      </PanelContainer>
    </Drawer>
  );
});

export default MenuPanel;